import React, { useEffect, useMemo, useState, useRef } from 'react';
import './HobbyMascot.css';

// Emoji map for the most common hobbies / interests.
// Keys are lowercase, hash-stripped, trimmed.
const HOBBY_EMOJI_MAP = {
  photography: '📷', photo: '📷', camera: '📸',
  fitness: '💪', gym: '🏋️', workout: '🏋️‍♀️',
  coding: '💻', code: '💻', programming: '👨‍💻', developer: '👨‍💻', dev: '💻',
  gaming: '🎮', games: '🕹️', game: '🎮',
  travel: '✈️', traveling: '🌍', adventure: '🗺️',
  coffee: '☕', espresso: '☕', latte: '🥛',
  foodie: '🍔', food: '🍽️',
  gallery: '🖼️', museum: '🏛️',
  tech: '🤖', ai: '🤖', robotics: '🤖',
  nature: '🌿', outdoors: '🌲',
  football: '⚽', soccer: '⚽',
  plants: '🌱', gardening: '🌷', garden: '🌻',
  meditation: '🧘', mindfulness: '🧘‍♂️', zen: '🕉️',
  creative: '🎨', creativity: '✨',
  cinema: '🎬', movies: '🍿', film: '🎞️',
  blogging: '✍️', blog: '📝',
  journalism: '📰', news: '🗞️',
  leadership: '🦁', business: '💼',
  music: '🎵', musician: '🎶',
  drawing: '✏️', sketching: '✏️', sketch: '🖌️',
  cats: '🐱', cat: '🐈', kitten: '🐾',
  dogs: '🐶', dog: '🐕', puppy: '🐩',
  books: '📚', reading: '📖', book: '📕',
  writing: '🖊️', poetry: '📜',
  yoga: '🧘‍♀️',
  running: '🏃', jogging: '🏃‍♂️', marathon: '🏅',
  cycling: '🚴', biking: '🚵', bike: '🚲',
  hiking: '🥾', mountains: '⛰️', climbing: '🧗',
  swimming: '🏊', surfing: '🏄', sailing: '⛵',
  skiing: '⛷️', snowboarding: '🏂',
  fishing: '🎣', camping: '🏕️',
  dance: '💃', dancing: '🕺',
  cooking: '🍳', baking: '🧁', chef: '👨‍🍳',
  art: '🎨', painting: '🖼️',
  guitar: '🎸', piano: '🎹', drums: '🥁', singing: '🎤', karaoke: '🎤',
  chess: '♟️', poker: '🃏', cards: '🂡',
  basketball: '🏀', tennis: '🎾', golf: '⛳', baseball: '⚾', volleyball: '🏐',
  boxing: '🥊', martialarts: '🥋', karate: '🥋',
  wine: '🍷', beer: '🍺', cocktails: '🍹',
  pizza: '🍕', sushi: '🍣', tea: '🍵', chocolate: '🍫', icecream: '🍦',
  anime: '🍙', manga: '📖',
  crypto: '🪙', bitcoin: '₿', ethereum: '◆', web3: '🌐', nft: '🖼️',
  science: '🔬', space: '🚀', astronomy: '🔭', physics: '⚛️',
  cars: '🚗', motorcycle: '🏍️',
  fashion: '👗', shopping: '🛍️', beauty: '💄', makeup: '💋',
  beach: '🏖️', surf: '🌊', diving: '🤿',
  birds: '🐦', horses: '🐴', fish: '🐠',
  rain: '🌧️', sun: '☀️', stars: '⭐',
  party: '🎉', festival: '🎪', concerts: '🎤',
  startup: '🚀', entrepreneur: '💡', investing: '📈',
  diy: '🔨', woodworking: '🪵', knitting: '🧶',
  vinyl: '💿', podcast: '🎙️', radio: '📻',
};

const normalize = (t) => String(t || '').trim().replace(/^#+/, '').toLowerCase().replace(/[\s_-]/g, '');

const MIN_INTERVAL_MS = 2500;
const MAX_INTERVAL_MS = 6000;
const VISIBLE_MS = 10000;

// Fisher–Yates shuffle (non-mutating)
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function HobbyMascot({ userTags = [], popularTags = [] }) {
  const matches = useMemo(() => {
    const popularSet = new Set(popularTags.map((p) => normalize(p?.tag ?? p)));
    const user = (userTags || []).map(normalize).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const t of user) {
      const emoji = HOBBY_EMOJI_MAP[t];
      if (!emoji) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.push({ tag: t, emoji, isPopular: popularSet.has(t) });
    }
    const popularOnly = out.filter((x) => x.isPopular);
    return popularOnly.length > 0 ? popularOnly : out;
  }, [userTags, popularTags]);

  const [active, setActive] = useState(null); // { id, tag, emoji, leftPct, anim, direction }
  const timerRef = useRef(null);
  const hideRef = useRef(null);
  const idRef = useRef(0);

  // Shuffled queue + cursor — preserves order across the cycle so we don't
  // repeat any mascot until the whole sequence has played once.
  const queueRef = useRef([]);
  const queueIndexRef = useRef(0);

  useEffect(() => {
    // Re-shuffle whenever the set of matches changes.
    queueRef.current = shuffle(matches);
    queueIndexRef.current = 0;
  }, [matches]);

  useEffect(() => {
    if (matches.length === 0) return undefined;

    const animations = ['anim-arc', 'anim-bounce', 'anim-peek', 'anim-rocket'];
    let cancelled = false;

    const showOne = () => {
      if (cancelled) return;
      if (queueRef.current.length === 0) return;

      // Advance cursor; when the cycle completes, restart from index 0
      // using the SAME shuffled order (per spec: "1-5-2-7-3 ... then from the start 1-5-2-7-3").
      const pick = queueRef.current[queueIndexRef.current];
      queueIndexRef.current = (queueIndexRef.current + 1) % queueRef.current.length;

      const anim = animations[Math.floor(Math.random() * animations.length)];
      idRef.current += 1;
      setActive({ id: idRef.current, tag: pick.tag, emoji: pick.emoji, anim });

      hideRef.current = setTimeout(() => {
        setActive(null);
        const next = MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
        timerRef.current = setTimeout(showOne, next);
      }, VISIBLE_MS);
    };

    timerRef.current = setTimeout(showOne, 2500);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, [matches]);

  if (!active) return null;

  return (
    <div
      key={active.id}
      className={`hobby-mascot ${active.anim}`}
      aria-hidden="true"
      title={`#${active.tag}`}
    >
      <span className="hobby-mascot-emoji">{active.emoji}</span>
      <span className="hobby-mascot-label">#{active.tag}</span>
    </div>
  );
}
